import FirstOnboardScreen from "../FirstOnboardScreen";
import SecondOnboardScreen from "../SecondOnboardScreen";
import ThirdOnboardScreen from "../ThirdOnboardScreen";
import GiftCardModal from "../GiftCardModal";
import RestoreGalleryScreen from "../RestoreGalleryScreen";
import ClickOut from "../ClickOut";
import React, {useState, useCallback, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {useRecoilState} from "recoil";
import {formUploadState, modalGiftState} from "../../../Recoil/Atoms";
import axios from "axios";
import { getCartDiscountEvent } from "../../../api/activePromoEvent";
import { getMergeCandidates, getImagesDB, logMergeEvent, mergeDebug } from "@api";
import { recordOnboardingChoice, onboardingCartFields, onboardingAbVariant } from "../../../api/onboardingChoice";
import { logGiftFlowEvent } from "../../../api/giftcard";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const MERGE_SNOOZE_KEY = "mergeSnoozeUntil";
const MERGE_SNOOZE_MS = 24 * 60 * 60 * 1000;

const OnboardScreens = ({ wall = false }) => {
    const [, setGift] = useRecoilState(modalGiftState);
    const [, setUploadState] = useRecoilState(formUploadState);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showGiftCard, setShowGiftCard] = useState(false);
    const [restoreData, setRestoreData] = useState(null); // gallery-merge candidate (Path A)
    const [restoreName, setRestoreName] = useState("");
    const navigate = useNavigate();

    // Complete onboarding the regular way (no existing gallery, or user skipped).
    const finishOnboarding = useCallback(() => {
        localStorage.setItem('loginCompleted', 1);
        setUploadState(true);
    }, [setUploadState]);

    /**
     * Called by ThirdOnboardScreen instead of finishing directly: once the cart
     * write completes, look for an existing live gallery for these details.
     * Found -> show the restore screen; not found -> finish as today.
     */
    const handleDetailsSubmitted = useCallback(async (values, cartWrite) => {
        // Candidate lookup is by email/phone from the form — it does NOT need a
        // sessionId (the server only uses one to exclude the current cart), so it
        // runs fully in parallel with the cart write: one round-trip of wait.
        // Only confirm() needs the cart to exist, and by the time the user has
        // typed an OTP the write has long landed (we still await it there).
        try {
            const [res] = await Promise.all([
                getMergeCandidates({ email: values.email, phone: values.phoneNumber, source: "onboarding" }),
                cartWrite.catch(() => null),
            ]);
            mergeDebug(`onboarding candidates -> ${JSON.stringify(res)}`);
            if (res && res.found) {
                setRestoreName((values.fullName || "").trim().split(" ")[0] || "");
                setRestoreData(res);
                return true; // caller keeps the upload UI hidden behind the restore screen
            }
        } catch (err) {
            mergeDebug(`ERROR candidate check failed: ${err.message}`);
        }
        finishOnboarding();
        return false;
    }, [finishOnboarding]);

    // OTP verified -> the old cart is now the active one. Swap identity + load it.
    const handleAdopted = useCallback(async (result) => {
        try {
            if (result && result.sessionId && result.uid) {
                localStorage.setItem("uniqueUserId", result.uid);
                localStorage.setItem("sessionId", result.sessionId);
                localStorage.setItem("dataExist", 1);
            }
            localStorage.setItem('loginCompleted', 1);
            await getImagesDB();
        } catch (err) {
            console.error("Failed to load restored gallery:", err.message);
        }
        setRestoreData(null);
        setUploadState(true);
        navigate('/review-your-images', {
            state: { mergeToast: 'הגלריה שלך חזרה ואפשר להמשיך מאיפה שעצרת' },
        });
    }, [navigate, setUploadState]);

    const handleRestoreSkip = useCallback(() => {
        // "Not now" — the Path B snackbar remains the fallback after later uploads.
        localStorage.setItem(MERGE_SNOOZE_KEY, String(Date.now() + MERGE_SNOOZE_MS));
        logMergeEvent("deferred", "onboarding");
        setRestoreData(null);
        finishOnboarding();
    }, [finishOnboarding]);

    const onboarding = localStorage.getItem('loginCompleted');
    const showing = parseInt(onboarding) !== 1;

    // A/B: name_first shows the details form before the for-me/gift question.
    // Only shoppers who actually see onboarding get an arm (and carry it on the cart).
    const [nameFirst] = useState(() => showing && onboardingAbVariant() === "name_first");
    useEffect(() => {
        if (showing) logGiftFlowEvent("onboarding_shown", { ab: nameFirst ? "name_first" : "gift_first" }, wall ? "wall" : "photos");
    }, [showing, nameFirst, wall]);
    // name_first: what the details step left for after the question — {values, cartWrite} or {skipped}
    const [savedDetails, setSavedDetails] = useState(null);

    const [show, setShow] = useState({
        showFirstScreen: false,
        showSecondScreen: !nameFirst,
        showThirdScreen: nameFirst,
        showClickOut: false,
        showSkip: false,
    });

    // Debug: say why the restore offer can never appear in this browser.
    if (parseInt(onboarding) === 1 && !window.__mergeGateLogged) {
        window.__mergeGateLogged = true;
        mergeDebug('onboarding gated off (loginCompleted=1) — restore screen cannot show; snackbar (Path B) is the only offer path. Clear localStorage.loginCompleted to retest.');
    }

    // Function to create a cart in the addtocart collection
    const createCart = useCallback(async (sessionId) => {
        try {
            const uniqueUserId = localStorage.getItem("uniqueUserId") || "unknown";
            const userEmail = localStorage.getItem("userEmail") || "";
            const fullName = localStorage.getItem("fullName") || "";
            const logIdentifier = userEmail ? `[${uniqueUserId}-${userEmail}]` : `[${uniqueUserId}]`;

            console.log(`${logIdentifier} Creating cart with sessionId: ${sessionId}`);

            // Create/update entry in addtocart collection with timeout
            const cartResponse = await axios.post(BASE_URL + "/addtocart", {
                uid: uniqueUserId,
                sessionId: sessionId,
                userInfo: {
                    fullName: fullName,
                    email: userEmail,
                    // the server replaces userInfo whole — keep the phone the details form saved
                    phoneNumber: localStorage.getItem("userPhone") || ""
                },
                frameType: "classic", // Default frame type
                discountEvent: getCartDiscountEvent(),
                ...onboardingCartFields()
            }, {
                timeout: 10000 // 10 second timeout
            });

            console.log(`${logIdentifier} Cart created successfully with sessionId: ${sessionId}`);
            return cartResponse;
        } catch (err) {
            console.error(`Error creating cart: ${err.message}`);
            // Don't throw - let the user continue even if cart creation fails
        }
    }, []);

    // Details-step "דלג": no contact captured; finish onboarding with a bare cart.
    const skipDetails = async () => {
        mergeDebug('onboarding SKIPPED — no contact captured, no candidate check (snackbar remains the fallback after uploads)');
        // Prevent multiple clicks
        if (isProcessing) {
            console.log('Skip already in progress, ignoring click');
            return;
        }

        setIsProcessing(true);

        try {
            // Generate a sessionId if one doesn't exist
            let sessionId = localStorage.getItem("sessionId");
            if (!sessionId) {
                // Use server-side session ID generation
                console.log(`🔄 [CLIENT-DEBUG] Requesting session ID from server API`);
                try {
                    const response = await axios.get(`${BASE_URL}/generate-session-id`);
                    if (response.data && response.data.sessionId) {
                        sessionId = response.data.sessionId;
                        console.log(`✅ [CLIENT-DEBUG] Server generated session ID: ${sessionId} (length: ${sessionId.length}, format: ${/^b[0-9a-z]{5}$/.test(sessionId) ? 'VALID' : 'INVALID'})`);
                    } else {
                        throw new Error('Invalid response from session generation API');
                    }
                } catch (error) {
                    console.error('❌ [CLIENT-DEBUG] Failed to generate session ID from server:', error.message);

                    // Fallback to client-side generation with new format
                    const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
                    sessionId = 'b';
                    for (let i = 0; i < 5; i++) {
                        sessionId += chars[Math.floor(Math.random() * chars.length)];
                    }
                    console.log(`🔄 [CLIENT-DEBUG] Generated fallback session ID: ${sessionId} (length: ${sessionId.length}, format: ${/^b[0-9a-z]{5}$/.test(sessionId) ? 'VALID' : 'INVALID'})`);
                }

                localStorage.setItem("sessionId", sessionId);
                console.log(`Stored new sessionId: ${sessionId}`);
            }

            // Close the modal immediately for better UX
            setShow({showThirdScreen: false});
            localStorage.setItem('loginCompleted', 1);
            setUploadState(true);

            // Create cart in background (non-blocking)
            createCart(sessionId).catch(err => {
                console.error('Failed to create cart:', err);
                // Cart creation failed but user can continue
            });
        } finally {
            setIsProcessing(false);
        }
    };

    // name_first arm. The details are saved the moment they're submitted (the contact
    // is captured even if the shopper drops at the question); the for-me/gift answer
    // comes after and is stamped on that same cart. Skipping the details still asks it.
    const handleNameFirstDetails = useCallback(async (values, cartWrite) => {
        setSavedDetails({ values, cartWrite });
        setShow((s) => ({ ...s, showSecondScreen: true }));
    }, []);
    const skipToQuestion = () => {
        setSavedDetails({ skipped: true });
        setShow((s) => ({ ...s, showThirdScreen: false, showSecondScreen: true }));
    };
    // After the answer: finish exactly as the details step finishes in the original order.
    const finishAfterQuestion = () => {
        if (!savedDetails || savedDetails.skipped) return skipDetails();
        const choiceWrite = savedDetails.cartWrite.then(() => createCart(localStorage.getItem("sessionId")));
        if (wall) return finishOnboarding();
        handleDetailsSubmitted(savedDetails.values, choiceWrite)
            .finally(() => setShow((s) => ({ ...s, showSecondScreen: false })));
    };

    return (parseInt(onboarding) !== 1) && (
        <div>
            {show.showFirstScreen && (
                <FirstOnboardScreen show={show.showFirstScreen}
                                    onClose={() => {
                                        setShow({ ...show, showFirstScreen: false, showSecondScreen: true});
                                    }}
                                    onClickOut={() => {
                                        setShow({...show, showClickOut: true,});
                                    }}
                />
            )}
            {show.showSecondScreen && (
                <SecondOnboardScreen show={show.showSecondScreen}
                                     step={nameFirst ? 2 : 1}
                                     firstOption={() => {
                                         recordOnboardingChoice("self", wall);
                                         setGift("self");
                                         if (nameFirst) return finishAfterQuestion();
                                         setShow({ ...show, showSecondScreen: false, showThirdScreen: true});
                                     }}
                                     secondOption={() => {
                                         recordOnboardingChoice("gift", wall);
                                         if (wall) {
                                             // Wall shoppers stay on the wall (owner, 2026-10-03): their
                                             // photos are uploading right now, and a wall for a relative is
                                             // a gift ORDER, not a gift card. Continue like "for me", with
                                             // the answer kept as gift (cart + order onboarding).
                                             setGift("gift");
                                             if (nameFirst) return finishAfterQuestion();
                                             setShow({ ...show, showSecondScreen: false, showThirdScreen: true});
                                             return;
                                         }
                                         // Photo flow: "Gift" leads to the gift-card PAGE (full flow with
                                         // the tier clips), not the trimmed modal.
                                         setShow({ ...show, showSecondScreen: false });
                                         navigate('/gift-card');
                                     }}
                                     onClickOut={() => {
                                         setShow({...show, showClickOut: true,});
                                     }}
                                     onSkip={() => {
                                         setShow({ ...show, showSecondScreen: false, showThirdScreen: true});
                                     }}
                />
            )}
            {show.showThirdScreen && (
                <ThirdOnboardScreen show={show.showThirdScreen}
                                    wall={wall}
                                    step={nameFirst ? 1 : 2}
                                    onDetailsSubmitted={nameFirst ? handleNameFirstDetails : (wall ? null : handleDetailsSubmitted)}
                                    onClose={() => {
                                        setShow((s) => ({ ...s, showThirdScreen: false }));
                                    }}
                                    onClickOut={() => {
                                        setShow({...show, showClickOut: true,});
                                    }}
                                    onSkip={nameFirst ? skipToQuestion : skipDetails}
                />
            )}
            {restoreData && (
                <RestoreGalleryScreen
                    data={restoreData}
                    firstName={restoreName}
                    onSkip={handleRestoreSkip}
                    onAdopted={handleAdopted}
                />
            )}
            {showGiftCard && (
                <GiftCardModal
                    onClose={() => {
                        // Back to the self/gift choice rather than a blank overlay.
                        setShowGiftCard(false);
                        setShow((s) => ({ ...s, showSecondScreen: true }));
                    }}
                    onSelfAdd={() => {
                        // "I'll add photos myself" — resume the original gift flow.
                        setShowGiftCard(false);
                        setGift("gift");
                        setShow((s) => ({ ...s, showSecondScreen: false, showThirdScreen: true }));
                    }}
                />
            )}
            <ClickOut show={show.showClickOut}
                      onClose={() => {
                          setShow({ ...show, showClickOut: false,});
                      }}
                      onCloseAll={async () => {
                          // Prevent multiple clicks
                          if (isProcessing) {
                              console.log('CloseAll already in progress, ignoring click');
                              return;
                          }

                          setIsProcessing(true);

                          try {
                              // Generate a sessionId if one doesn't exist
                              let sessionId = localStorage.getItem("sessionId");
                              if (!sessionId) {
                                  // Use server-side session ID generation
                                  try {
                                      const response = await axios.get(`${BASE_URL}/generate-session-id`);
                                      if (response.data && response.data.sessionId) {
                                          sessionId = response.data.sessionId;
                                          console.log(`Server generated session ID: ${sessionId}`);
                                      } else {
                                          throw new Error('Invalid response from session generation API');
                                      }
                                  } catch (error) {
                                      console.error('Failed to generate session ID from server:', error.message);

                                      // Fallback to client-side generation with new format
                                      const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
                                      sessionId = 'b';
                                      for (let i = 0; i < 5; i++) {
                                          sessionId += chars[Math.floor(Math.random() * chars.length)];
                                      }
                                      console.log(`Generated fallback session ID: ${sessionId}`);
                                  }

                                  localStorage.setItem("sessionId", sessionId);
                                  console.log(`Stored new sessionId: ${sessionId}`);
                              }

                              // Close the modal immediately for better UX
                              setShow({showClickOut: false});
                              setUploadState(true);
                              localStorage.setItem('loginCompleted', 1);

                              // Create cart in background (non-blocking)
                              createCart(sessionId).catch(err => {
                                  console.error('Failed to create cart:', err);
                                  // Cart creation failed but user can continue
                              });
                          } finally {
                              setIsProcessing(false);
                          }
                      }}
            />
        </div>
    )
}

export default OnboardScreens;
